/**
 * Mevkiler: haritada adı konmuş bölgeler (stadyum, köşk, köy evi).
 *
 * Stratejik noktalarla aynı dünya uzayında durur (0..1080) ve aynı kuralla
 * yazılır: herkes ekler, sahibi ve yönetim düzenler. Yetkiyi backend hesaplar
 * (`can_edit`), arayüz rolü ikinci kez yorumlamaz.
 */

export interface CalloutVertex { x: number; y: number }

export interface Callout {
  id: number
  map_name: string
  name: string
  points: CalloutVertex[]
  created_by: string
  created_by_name: string
  created_by_avatar: string
  created_at: number
  updated_at: number
  can_edit: boolean
}

/**
 * **Tek renk, sabit dolgu.** Mevki bir çizim değil haritanın bir parçası: her
 * slaytta ve her kişide aynı görünmeli. Renk seçilebilseydi aynı stadyum iki
 * ekranda iki ayrı şey gibi okunurdu. Turuncu, kızıla (tema, düşman) ve
 * beyaza (kontrol bölgesi, kuleler) karışmayan renk.
 */
export const CALLOUT_COLOR = '#f97316'
export const CALLOUT_FILL_OPACITY = 0.16

export const CALLOUT_NAME_MAX = 60

export async function fetchCallouts(mapName: string): Promise<Callout[]> {
  const res = await fetch(`/api/callouts?map_name=${encodeURIComponent(mapName)}`, { credentials: 'include' })
  return res.ok ? res.json() : []
}

/** Çokgenin alanı (işaretsiz). */
export function polygonArea(pts: CalloutVertex[]): number {
  let a = 0
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i], q = pts[(i + 1) % pts.length]
    a += p.x * q.y - q.x * p.y
  }
  return Math.abs(a / 2)
}

/**
 * Adın oturacağı yer: alan ağırlık merkezi. İçbükey bir çokgende (L biçimli
 * bir köy) merkez dışarı düşebilir; o zaman köşelerin ortalaması denenir,
 * o da dışarıdaysa ilk köşe. Ad her zaman kendi bölgesinin üstünde durmalı.
 */
export function polygonLabelPoint(pts: CalloutVertex[]): CalloutVertex {
  let a = 0, cx = 0, cy = 0
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i], q = pts[(i + 1) % pts.length]
    const cross = p.x * q.y - q.x * p.y
    a += cross
    cx += (p.x + q.x) * cross
    cy += (p.y + q.y) * cross
  }
  if (Math.abs(a) > 1e-9) {
    const c = { x: cx / (3 * a), y: cy / (3 * a) }
    if (pointInPolygon(c, pts)) return c
  }
  const avg = {
    x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
    y: pts.reduce((s, p) => s + p.y, 0) / pts.length,
  }
  return pointInPolygon(avg, pts) ? avg : pts[0]
}

export function pointInPolygon(pt: CalloutVertex, pts: CalloutVertex[]): boolean {
  let inside = false
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i], b = pts[j]
    if ((a.y > pt.y) !== (b.y > pt.y) &&
        pt.x < ((b.x - a.x) * (pt.y - a.y)) / (b.y - a.y) + a.x) inside = !inside
  }
  return inside
}

/**
 * Tıklanan yerdeki mevki. İç içe mevkilerde (köyün içindeki bir ev) **en
 * küçüğü** kazanır: büyük olanın her yerine tıklanabilir, küçük olanın yalnızca
 * kendi içine. Üsttekini seçmek küçüğü hiç seçilemez yapardı.
 */
export function calloutAt(pt: CalloutVertex, callouts: Callout[]): Callout | null {
  let best: Callout | null = null
  let bestArea = Infinity
  for (const c of callouts) {
    if (c.points.length < 3 || !pointInPolygon(pt, c.points)) continue
    const area = polygonArea(c.points)
    if (area < bestArea) { best = c; bestArea = area }
  }
  return best
}
